import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type {
  AuctionsOfNetworkPointOrBundleParams,
  AuctionsOfNetworkPointOrBundleResponse,
} from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useAxios } from 'src/hooks/use-axios';

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  networkPointOrBundleId: Uuid;
  params: AuctionsOfNetworkPointOrBundleParams;
};

type ExecuteOptions = {
  url: string;
  params: AuctionsOfNetworkPointOrBundleParams;
};

export function useAuctionsOfNetworkPointOrBundle({
  root,
  networkPointOrBundleId,
  params,
}: HookOptions) {
  const auctions = useAxios(
    (axios, baseConfig, { url, params }: ExecuteOptions) =>
      axios.request<AuctionsOfNetworkPointOrBundleResponse>({
        ...baseConfig,
        url,
        params,
      }),
    { neededOnPageLoad: true }
  );

  const auctionsLink =
    root?.response?.data._links.auctionsOfNetworkPointOrBundle;

  useEffect(() => {
    if (!auctionsLink) return;

    const url = urlTemplate
      .parse(auctionsLink.href)
      .expand({ networkPointOrBundleId });
    auctions.execute({ url, params });
    // oxlint-disable-next-line exhaustive-deps
  }, [auctionsLink, networkPointOrBundleId, params]);

  if (!auctionsLink) return;

  return auctions;
}

export type AuctionsOfNetworkPointOrBundleRequest = ReturnType<
  typeof useAuctionsOfNetworkPointOrBundle
>;
